  import { useNavigate } from "react-router-dom";
  import CRMLayout from "../components/crm/CRMLayout";
  import HelpChatbot from "../components/HelpChatbot/HelpChatbot";
  import { Briefcase, CheckCircle2, CalendarDays, BarChart3, X, RotateCcw, ChevronLeft, ChevronRight } from "lucide-react";
  import MetricCardForm from "../components/MetricCardForm";
  // dashboard.css was previously pulled in via the StatsCards import; the
  // layout classes (.dashboard-main etc.) live there, so import it directly.
  import "../components/crm/dashboard/dashboard.css";
  import { useState, useEffect, useRef } from "react";
  import { db } from '../firebase';
  import { collection, deleteDoc, doc, getDocs, query, where, onSnapshot, writeBatch, setDoc, getDoc, updateDoc, deleteField, Timestamp } from "firebase/firestore";
  import { useAuth } from "../context/AuthContext";
  import toast from "react-hot-toast";
  import { groupForSubcard, filterCases, SUBCARD_FIELDS } from "../utils/metricEngine";
  import { statusLabel } from "../utils/caseStatus";
  import { getUserLabels } from "../services/labelService";
  import featureGatingService from "../services/featureGatingService";
  import CaseLimitDialog from "../components/CaseLimitDialog";
  import AIChatbotUpgradeDialog from "../components/AIChatbotUpgradeDialog";
  import ProfileCompletionPopup, { isProfileIncomplete } from "../components/ProfileCompletionPopup";
  import FeatureLockedDialog from "../components/FeatureLockedDialog";
  import useFeatureFlags from "../utils/useFeatureFlags";
  import AnnouncementPopup from "../components/AnnouncementPopup";
  import CelebrationModal from "../components/CelebrationModal";
  import { getActiveAnnouncementForUser } from "../services/announcementService";
  import CaseModal from "./AddMetric";
  import EditCaseModal from "./EditMetic";
  import UpcomingHearings from "../components/crm/dashboard/UpcomingHearings";
  import TasksDueToday from "../components/crm/dashboard/TasksDueToday";
  import CaseOverview from "../components/crm/dashboard/CaseOverview";
  import RecentCases from "../components/crm/dashboard/RecentCases";
  import MyActivity from "../components/crm/dashboard/MyActivity";
  import RecentUpdates from "../components/crm/dashboard/RecentUpdates";
  import QuickActions from "../components/crm/dashboard/QuickActions";

  // Reusable lock badge shown on disabled cards — matches existing AI chatbot lock style.
  const LockBadge = () => (
    <div className="absolute top-2 right-2 w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center">
      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
    </div>
  );

  function Dashboard() {
    const { globalUser, globalData, isLawStudent } = useAuth();
    const navigate = useNavigate();
    const isClient = localStorage.getItem("userType") === "client";
    const { isEnabled } = useFeatureFlags(globalUser?.uid);
    const [pendingCases, setPendingCases] = useState([]);
    const [disposedCases, setDisposedCases] = useState([]);
    const [todayCasesCount, setTodayCasesCount] = useState(0);
    const [todayCasesList, setTodayCasesList] = useState([]);
    // Card whose stats modal is open: { title, cases, route, card?, basis? } or null.
    const [statsCard, setStatsCard] = useState(null);
    // Per-user customisation of the built-in cards (colour / stats basis).
    // Stored in the metricCard collection as { builtinKey, ... } docs so no
    // new collection (and no new Firestore rules) are needed.
    const [builtinPrefs, setBuiltinPrefs] = useState({});
    // Built-in card being edited: { key, title, accentColor, subcardField } or null.
    const [editBuiltin, setEditBuiltin] = useState(null);
    const [totalCase, setTotalCase] = useState([]);
    const [disposedCasesCount, setDisposedCasesCount] = useState(0);
    const [metricCardList, setMetricCardList] = useState([]);
    const [showAddMetric, setShowAddMetric] = useState(false);
    const [editMetricId, setEditMetricId] = useState(null);
    const [labelsMap, setLabelsMap] = useState({});
    const [orderDirty, setOrderDirty] = useState(false);
    const [showResetConfirm, setShowResetConfirm] = useState(false);
    const dragIndex = useRef(null);
    // Metric strip scroll hints: the row scrolls horizontally once there are
    // more than 4 cards, so a slim scrollbar + edge fades signal the overflow.
    const metricsRowRef = useRef(null);
    const [metricsOverflow, setMetricsOverflow] = useState({ left: false, right: false });
    const [sharedCaseNotifications, setSharedCaseNotifications] = useState([]);
    const [sharedCaseNotificationCount, setSharedCaseNotificationCount] = useState(0);
    const [referralCount, setReferralCount] = useState(0);
    const [showWelcomePopup, setShowWelcomePopup] = useState(false);
    // Duration (in days) shown in the celebration popup, and whether it was
    // triggered by an admin-assigned free plan (server flag) vs. the first-time
    // welcome — the former must clear the flag on close so it shows only once.
    const [welcomeDays, setWelcomeDays] = useState(0);
    const [celebrationFromFlag, setCelebrationFromFlag] = useState(false);

    // Case limit states
    const [isCaseLimitReached, setIsCaseLimitReached] = useState(false);
    const [showCaseLimitDialog, setShowCaseLimitDialog] = useState(false);
    const [caseLimitData, setCaseLimitData] = useState({
      currentCount: 0,
      maxLimit: 0,
      planName: ''
    });

    // AI Chatbot access states
    const [canAccessAIChatbot, setCanAccessAIChatbot] = useState(true);
    const [showAIChatbotDialog, setShowAIChatbotDialog] = useState(false);

    // Profile completion popup state
    const [showProfilePopup, setShowProfilePopup] = useState(false);

    // In-app announcement popup (authored in the admin portal)
    const [announcement, setAnnouncement] = useState(null);

    // Generic locked-feature dialog (admin-disabled features)
    const [lockedFeatureName, setLockedFeatureName] = useState(null);
    const showLocked = (name) => (e) => {
      if (e) e.preventDefault();
      setLockedFeatureName(name);
    };

    // Which side of the metric strip still has hidden cards. The 4px slack
    // absorbs sub-pixel scroll offsets so the fade doesn't flicker at the ends.
    const syncMetricsOverflow = () => {
      const el = metricsRowRef.current;
      if (!el) return;
      const max = el.scrollWidth - el.clientWidth;
      setMetricsOverflow({
        left: el.scrollLeft > 4,
        right: max > 4 && el.scrollLeft < max - 4,
      });
    };

    // Re-measure when the card count changes (cards added/removed/reset) and on
    // resize, since the strip only overflows at narrower widths.
    useEffect(() => {
      syncMetricsOverflow();
      window.addEventListener("resize", syncMetricsOverflow);
      return () => window.removeEventListener("resize", syncMetricsOverflow);
    }, [metricCardList.length]);

    const scrollMetrics = (dir) => {
      const el = metricsRowRef.current;
      if (el) el.scrollBy({ left: dir * 260, behavior: "smooth" });
    };

    // Load user labels once for multilevel-card breakdown names.
    useEffect(() => {
      if (!globalUser?.uid) return;
      getUserLabels(globalUser.uid)
        .then((ls) => setLabelsMap(Object.fromEntries(ls.map((l) => [l.id, l.name]))))
        .catch(() => {});
    }, [globalUser]);

    // Load the active in-app announcement (if any) for this user.
    useEffect(() => {
      if (!globalUser?.uid) return;
      const userType = localStorage.getItem("userType") === "client" ? "client" : "lawyers";
      getActiveAnnouncementForUser(globalUser.uid, userType)
        .then(setAnnouncement)
        .catch((err) => console.warn("Failed to load announcement:", err?.message || err));
    }, [globalUser]);

    useEffect(() => {
      const fetchMetricCards = async () => {
        if (!globalUser) return;
        const metricCollection = collection(db, "metricCard");
        const metricQuery = query(metricCollection, where("owner", "==", globalUser.uid));
        const metricSnapshot = await getDocs(metricQuery);
        const metricList = metricSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        // builtinKey docs customise the built-in cards; the rest are custom cards.
        const prefs = {};
        const customs = [];
        metricList.forEach((c) => (c.builtinKey ? (prefs[c.builtinKey] = c) : customs.push(c)));
        customs.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        setMetricCardList(customs);
        setBuiltinPrefs(prefs);
      };

      const fetchAllCases = async () => {
        if (!globalUser) return;
        let caseList = [];
        const caseStatus = ["pending", "disposed"];

        for (const status of caseStatus) {
          const caseCollection = collection(db, status);
          const caseQuery = query(caseCollection, where("owner", "==", globalUser.uid));
          const caseSnapShot = await getDocs(caseQuery);
          const tempCaseList = caseSnapShot.docs.map(doc => ({ id: doc.id, status: status, ...doc.data() }));
          caseList = [...caseList, ...tempCaseList];
        }
        setTotalCase(caseList);
      };

      const fetchCases = async () => {
        if (!globalUser) return;

        const pendingCollection = collection(db, "pending");
        const pendingQuery = query(pendingCollection, where("owner", "==", globalUser.uid));
        const pendingSnapshot = await getDocs(pendingQuery);
        const pendingList = pendingSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPendingCases(pendingList);

        const disposedCollection = collection(db, "disposed");
        const disposedQuery = query(disposedCollection, where("owner", "==", globalUser.uid));
        const disposedSnapshot = await getDocs(disposedQuery);
        const disposedList = disposedSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setDisposedCases(disposedList);

        setDisposedCasesCount(disposedList.length);
      };

      fetchCases();
      fetchAllCases();
      fetchMetricCards();
    }, [globalUser]);

    // Compute today's cases count
    useEffect(() => {
      if (!totalCase || totalCase.length === 0) {
        setTodayCasesCount(0);
        setTodayCasesList([]);
        return;
      }
      const today = new Date();
      const todayStr = `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${today.getFullYear()}`;
      const list = totalCase.filter((c) => c.nextHearingDate === todayStr);
      setTodayCasesList(list);
      setTodayCasesCount(list.length);
    }, [totalCase]);

    // Fetch and count shared case notifications in real-time
    useEffect(() => {
      if (!globalUser) return;

      const userCollection = isClient ? "client" : "lawyers";
      const userDocRef = doc(db, userCollection, globalUser.uid);
      const notificationsRef = collection(userDocRef, "notifications");
      const unsubscribe = onSnapshot(notificationsRef, (snapshot) => {
        const notifs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        const sharedCaseNotifs = notifs.filter((notif) => notif.title === "Case Shared With You");
        setSharedCaseNotifications(sharedCaseNotifs);
        setSharedCaseNotificationCount(sharedCaseNotifs.length);
      });

      return () => unsubscribe();
    }, [globalUser]);

    // Count unseen resume referrals (practising lawyers only) for the card badge.
    useEffect(() => {
      if (!globalUser?.uid || isClient || isLawStudent) return;

      const referralsQuery = query(
        collection(db, "referrals"),
        where("lawyerId", "==", globalUser.uid),
        where("seenByLawyer", "==", false),
      );
      const unsubscribe = onSnapshot(
        referralsQuery,
        (snapshot) => setReferralCount(snapshot.size),
        (err) => console.warn("Referral badge listener failed:", err?.message || err),
      );

      return () => unsubscribe();
    }, [globalUser, isClient, isLawStudent]);

    // Check if lawyer profile is incomplete and show mandatory popup
    useEffect(() => {
      if (!globalUser?.uid || isClient) return;
      const checkProfile = async () => {
        try {
          const snap = await getDoc(doc(db, "lawyers", globalUser.uid));
          if (snap.exists() && isProfileIncomplete(snap.data())) {
            setShowProfilePopup(true);
          }
        } catch (err) {
          console.error("Error checking profile:", err);
        }
      };
      checkProfile();
    }, [globalUser, isClient]);

    // Check and show first-time subscription popup
    useEffect(() => {
      const checkAndShowFirstTimeSubscription = async () => {
        if (!globalUser) return;

        try {
          const subscriptionDocRef = doc(db, "subscriptions", globalUser.uid);

          // Length (in days) actually granted by a "free" transaction.
          const freeTxDays = (tx) => {
            try {
              const toDate = (v) => (v?.toDate ? v.toDate() : new Date(v));
              const days = Math.round(
                (toDate(tx['end-time']).getTime() - toDate(tx['start-time']).getTime()) / 86400000
              );
              return days > 0 ? days : 0;
            } catch (e) {
              return 0;
            }
          };

          // Read the subscription once and reuse it for both the flag and the
          // first-time welcome checks.
          const subscriptionDoc = await getDoc(subscriptionDocRef);
          const subData = subscriptionDoc.exists() ? subscriptionDoc.data() : null;

          // --- 1. Admin-assigned free plan (highest priority) -------------------
          // Set by jradmin when an admin grants a free plan. Shows even for
          // returning users who already saw the first-time welcome; cleared on
          // close so it appears only once per grant.
          const pendingFlag = subData?.pendingFreeCelebration;
          if (pendingFlag && pendingFlag.durationDays) {
            setWelcomeDays(Number(pendingFlag.durationDays));
            setCelebrationFromFlag(true);
            setShowWelcomePopup(true);
            return;
          }

          // --- 2. First-time welcome (once per device via localStorage) ---------
          const hasSeenWelcomePopup = localStorage.getItem('hasSeenWelcomePopup');
          console.log('Checking first time subscription, hasSeenWelcomePopup:', hasSeenWelcomePopup);

          if (hasSeenWelcomePopup === null) {
            let grantedDays = 0;

            if (!subscriptionDoc.exists()) {
              // Create subscription if it doesn't exist (legacy fallback path)
              try {
                const userDocRef = doc(db, "lawyers", globalUser.uid);
                const userDoc = await getDoc(userDocRef);

                let startdate = new Date();

                if (userDoc.exists() && userDoc.data()) {
                  const userData = userDoc.data();
                  if (userData.createdAt) {
                    try {
                      startdate = userData.createdAt.toDate();
                    } catch (e) {
                      console.log('Error parsing createdAt timestamp:', e);
                      startdate = new Date();
                    }
                  } else {
                    console.log('Warning: createdAt field not found, using current date');
                  }
                } else {
                  console.log('Warning: User document not found or empty, using current date');
                }

                const endDate = new Date(startdate);
                endDate.setDate(endDate.getDate() + 30);
                grantedDays = 30;

                await setDoc(subscriptionDocRef, {
                  transactions: [
                    {
                      userCateogry: userDoc.data()?.userCateogry || '',
                      type: 'free',
                      'start-time': Timestamp.fromDate(startdate),
                      'end-time': Timestamp.fromDate(endDate),
                    }
                  ]
                });

                localStorage.setItem('end-date', endDate.toString());
                console.log('Free subscription created successfully');
              } catch (e) {
                console.log('Error creating subscription:', e);
              }
            } else {
              // Subscription exists, check if it's a free subscription
              try {
                let freeTx = null;

                if (subData && subData.transactions) {
                  for (const transaction of subData.transactions) {
                    if (transaction.type === 'free') {
                      freeTx = transaction;
                      break;
                    }
                  }
                }

                // If user doesn't have a free subscription, don't show the popup
                if (!freeTx) {
                  console.log('User has paid subscription, skipping welcome popup');
                  localStorage.setItem('hasSeenWelcomePopup', 'false');
                  return;
                }

                grantedDays = freeTxDays(freeTx);
              } catch (e) {
                console.log('Error checking subscription type:', e);
              }
            }

            // Show congratulations dialog for fresh install with free subscription
            setWelcomeDays(grantedDays);
            setCelebrationFromFlag(false);
            setShowWelcomePopup(true);
          }
        } catch (error) {
          console.log('Error in subscription check:', error);
          localStorage.setItem('hasSeenWelcomePopup', 'false');
        }
      };

      checkAndShowFirstTimeSubscription();
    }, [globalUser]);

    // Check case limit status
    useEffect(() => {
      const checkCaseLimitStatus = async () => {
        if (!globalUser?.uid) return;

        try {
          const currentCount = pendingCases.length;
          const maxLimit = await featureGatingService.getMaxCaseExportLimit(globalUser.uid);
          const planName = await featureGatingService.getPlanDisplayName(globalUser.uid);

          setIsCaseLimitReached(currentCount >= maxLimit);
          setCaseLimitData({
            currentCount,
            maxLimit,
            planName
          });
        } catch (error) {
          console.error('Error checking case limit:', error);
        }
      };

      checkCaseLimitStatus();
    }, [globalUser, pendingCases]);

    // Check AI chatbot access
    useEffect(() => {
      const checkAIChatbotAccess = async () => {
        if (!globalUser?.uid) return;

        try {
          const hasAccess = await featureGatingService.canAccessAIChatbot(globalUser.uid);
          setCanAccessAIChatbot(hasAccess);
        } catch (error) {
          console.error('Error checking AI chatbot access:', error);
          setCanAccessAIChatbot(true); // Allow on error
        }
      };

      checkAIChatbotAccess();
    }, [globalUser]);

    // Check and show case limit dialog
    const checkCaseLimitAndShowDialog = async () => {
      if (!globalUser?.uid) return false;

      try {
        const currentCount = pendingCases.length;
        const maxLimit = await featureGatingService.getMaxCaseExportLimit(globalUser.uid);

        if (currentCount >= maxLimit) {
          const planName = await featureGatingService.getPlanDisplayName(globalUser.uid);
          setCaseLimitData({
            currentCount,
            maxLimit,
            planName
          });
          setShowCaseLimitDialog(true);
          return false; // Limit exceeded
        }
        return true; // Can proceed
      } catch (error) {
        console.error('Error checking case limit:', error);
        return true; // Allow on error
      }
    };

    // Handle add case navigation with limit check
    const handleAddCaseClick = async (e) => {
      e.preventDefault();
      const canProceed = await checkCaseLimitAndShowDialog();
      if (canProceed) {
        navigate('/portal/addcase');
      }
    };

    // Handle find cases navigation with limit check
    const handleFindCasesClick = async (e) => {
      e.preventDefault();
      const canProceed = await checkCaseLimitAndShowDialog();
      if (canProceed) {
        navigate('/portal/findCasesOptions');
      }
    };

    // Handle Jurident Assistance navigation with AI chatbot access check
    const handleJuridentAssistanceClick = async (e) => {
      e.preventDefault();
      if (!canAccessAIChatbot) {
        setShowAIChatbotDialog(true);
      } else {
        navigate('/portal/JuriAssistance');
      }
    };

    const handleCloseWelcomePopup = async () => {
      setShowWelcomePopup(false);
      localStorage.setItem('hasSeenWelcomePopup', 'false');
      // For an admin-assigned grant, clear the server flag so it shows only once.
      if (celebrationFromFlag && globalUser?.uid) {
        try {
          await updateDoc(doc(db, "subscriptions", globalUser.uid), {
            pendingFreeCelebration: deleteField(),
          });
        } catch (e) {
          console.log('Error clearing pendingFreeCelebration flag:', e);
        }
        setCelebrationFromFlag(false);
      }
    };

    const handleDelete = async (id) => {
      try {
        const newArr = metricCardList.filter((item) => item.id !== id);
        setMetricCardList(newArr);
        await deleteDoc(doc(db, "metricCard", id));
        toast.success("Card deleted successfully");
      } catch (error) {
        console.log(error);
      }
    };

    const refreshMetricCards = async () => {
      if (!globalUser) return;
      const metricCollection = collection(db, "metricCard");
      const metricQuery = query(metricCollection, where("owner", "==", globalUser.uid));
      const metricSnapshot = await getDocs(metricQuery);
      const list = metricSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      const prefs = {};
      const customs = [];
      list.forEach((c) => (c.builtinKey ? (prefs[c.builtinKey] = c) : customs.push(c)));
      customs.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setMetricCardList(customs);
      setBuiltinPrefs(prefs);
      setOrderDirty(false);
    };

    // ---- Metric card reordering (native drag & drop) ----
    const handleDragStart = (index) => { dragIndex.current = index; };
    const handleDragOver = (e) => e.preventDefault();
    const handleDrop = (index) => {
      const from = dragIndex.current;
      dragIndex.current = null;
      if (from == null || from === index) return;
      setMetricCardList((prev) => {
        const next = [...prev];
        const [moved] = next.splice(from, 1);
        next.splice(index, 0, moved);
        return next;
      });
      setOrderDirty(true);
    };

    const handleSaveOrder = async () => {
      try {
        const batch = writeBatch(db);
        metricCardList.forEach((card, idx) => batch.update(doc(db, "metricCard", card.id), { order: idx }));
        await batch.commit();
        setOrderDirty(false);
        toast.success("Order saved");
      } catch (error) {
        console.error(error);
        toast.error("Failed to save order");
      }
    };

    // Reset to default = remove all custom metric cards and clear the built-in
    // cards' customisations (colour / stats basis).
    const handleResetDefault = async () => {
      try {
        const batch = writeBatch(db);
        metricCardList.forEach((card) => batch.delete(doc(db, "metricCard", card.id)));
        Object.values(builtinPrefs).forEach((p) => batch.delete(doc(db, "metricCard", p.id)));
        await batch.commit();
        setMetricCardList([]);
        setBuiltinPrefs({});
        setOrderDirty(false);
        setShowResetConfirm(false);
        toast.success("Metric cards reset to default");
      } catch (error) {
        console.error(error);
        toast.error("Failed to reset metric cards");
      }
    };

    // Persist a built-in card's customisation from the shared MetricCardForm
    // (filters hidden — the case list is fixed). Deterministic doc id keeps one
    // prefs doc per card; merge so future fields survive.
    const saveBuiltinCard = async (formData) => {
      if (!globalUser?.uid || !editBuiltin) return;
      try {
        await setDoc(
          doc(db, "metricCard", `${globalUser.uid}_${editBuiltin.key}`),
          {
            owner: globalUser.uid,
            builtinKey: editBuiltin.key,
            title: formData.title,
            description: formData.description,
            accentColor: formData.accentColor,
            bgColor: formData.bgColor,
            multilevel: formData.multilevel,
            subcardField: formData.subcardField,
          },
          { merge: true }
        );
        setEditBuiltin(null);
        refreshMetricCards();
        toast.success("Card updated");
      } catch (error) {
        console.error(error);
        toast.error("Failed to update card");
      }
    };

    // Delete a built-in card's prefs doc → back to its default look/behaviour.
    const resetBuiltinCard = async (key) => {
      if (!globalUser?.uid) return;
      try {
        const existing = builtinPrefs[key];
        if (existing?.id) await deleteDoc(doc(db, "metricCard", existing.id));
        refreshMetricCards();
        toast.success("Card reset to default");
      } catch (error) {
        console.error(error);
        toast.error("Failed to reset card");
      }
    };

    // Function to delete shared case notifications
    const deleteSharedCaseNotifications = async () => {
      if (!globalUser || sharedCaseNotifications.length === 0) return;

      const batch = writeBatch(db);
      sharedCaseNotifications.forEach((notif) => {
        const userCollection = isClient ? "client" : "lawyers";
        const notifRef = doc(db, userCollection, globalUser.uid, "notifications", notif.id);
        batch.delete(notifRef);
      });
      await batch.commit();
      console.log("Shared case notifications deleted.");
    };

    // Shorten long court names for the compact "Hearings This Week" breakdown.
    const shortCourt = (name) => {
      if (!name) return "—";
      let s = String(name).trim();
      if (/^National Green Tribunal\b/i.test(s)) return "NGT";
      if (/^National Company Law Tribunal\b/i.test(s)) return "NCLT";
      const hcOf = s.match(/^(?:Hon['’]?ble\s+)?High Court of ([A-Za-z .]+?)(?:\s*[-–,(].*)?$/i);
      if (hcOf) return `${hcOf[1].trim()} HC`;
      s = s.replace(/\bHigh Court\b/gi, "HC");
      s = s.replace(/\bSupreme Court of India\b/gi, "Supreme Court");
      s = s.replace(/\bSpecial Judge\b/gi, "Spl. Judge");
      s = s.replace(/\s*\(PC Act\)/gi, "");
      s = s.replace(/\bHC\b[\s,(].*$/i, "HC"); // drop bench/location after "… HC"
      return s.replace(/\s{2,}/g, " ").trim() || String(name);
    };

    // Group a case list by a field -> [{ label, value }] sorted by count desc.
    // Drives the stat cards' inline breakdown chips and the stats modal.
    const groupCounts = (cases, field) => {
      const m = new Map();
      (cases || []).forEach((c) => {
        const v = c?.[field] == null || c[field] === "" ? "—" : String(c[field]);
        m.set(v, (m.get(v) || 0) + 1);
      });
      return [...m.entries()]
        .map(([label, value]) => ({ label, value }))
        .sort((a, b) => b.value - a.value);
    };

    // Like groupCounts, but understands the "label" pseudo-field (labels are
    // arrays + labelsByUser, named via labelsMap) and normalizes status values
    // ("pending" / "Pending" → one "Pending" row) used as a stats basis.
    const groupField = (cases, field) => {
      if (field === "status") {
        const m = new Map();
        (cases || []).forEach((c) => {
          const raw = c?.status == null || c.status === "" ? "—" : String(c.status);
          const name = statusLabel(raw) || raw;
          m.set(name, (m.get(name) || 0) + 1);
        });
        return [...m.entries()]
          .map(([label, value]) => ({ label, value }))
          .sort((a, b) => b.value - a.value);
      }
      if (field !== "label") return groupCounts(cases, field);
      const m = new Map();
      (cases || []).forEach((c) => {
        const ids = new Set([
          ...(c.labels || []),
          ...(((c.labelsByUser || {})[globalUser?.uid]) || []),
        ]);
        ids.forEach((id) => {
          const name = labelsMap[id] || "Label";
          m.set(name, (m.get(name) || 0) + 1);
        });
      });
      return [...m.entries()]
        .map(([label, value]) => ({ label, value }))
        .sort((a, b) => b.value - a.value);
    };

    // Total stat cards = 3 built-in (Pending / Disposed / Today) + custom metric
    // cards. ≤4 cards stay centered on the row; >4 switch to a single, left-aligned
    // horizontally-scrolling row (no wrapping to a second row).
    const totalCards = 3 + metricCardList.length;
    const scrollCards = totalCards > 4;

    return (
      <>
        <CRMLayout
          style={{
            background: "#f5f7fa",
            minHeight: "100vh",
            padding: "8px 20px 20px",
          }}
        >
          {/* Unified stats row — the built-in Pending/Disposed/Today counts and
              the user's custom metric cards share one StatsCards-style row.
              Clicking a card body opens its stats modal; the "View all" link
              navigates. Add Metric / Reset are pinned top-right. */}
          <div className="px-1 pt-2">
            <div className="mb-2 flex items-center justify-end gap-3">
              {orderDirty && (
                <button
                  onClick={handleSaveOrder}
                  className="px-3 py-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium"
                >
                  Save Order
                </button>
              )}
              <button
                onClick={() => setShowAddMetric(true)}
                className="px-3 py-1.5 rounded-full bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold whitespace-nowrap text-xs"
              >
                Add Metric
              </button>
              {(metricCardList.length !== 0 || Object.keys(builtinPrefs).length !== 0) && (
                <button
                  onClick={() => setShowResetConfirm(true)}
                  className="px-3 py-1.5 rounded-full border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 font-semibold whitespace-nowrap text-xs shadow-sm"
                >
                  Reset
                </button>
              )}
            </div>

            {/* ≤4 cards: centered, wrapping row. >4 cards: single left-aligned row
                that scrolls horizontally (never wraps to a second row). Top
                padding keeps the cards' hover edit controls (top:-10px) from
                being clipped by the scroll container's overflow. */}
            <div className="relative">
            <div
              ref={metricsRowRef}
              onScroll={scrollCards ? syncMetricsOverflow : undefined}
              className={`flex gap-3 ${
                scrollCards
                  ? "metric-strip flex-nowrap justify-start overflow-x-auto pt-3 pb-2"
                  : "metric-wrap flex-wrap justify-center"
              }`}
            >
              {[
                // Default colours follow the old top metric strip: green / red / blue.
                { key: "pendingCases", title: "Pending Cases", list: pendingCases, Icon: Briefcase, iconBg: "#E9F9EF", iconColor: "#16A34A", route: "/portal/pendingcases/pending", action: "View all" },
                { key: "disposedCases", title: "Disposed Cases", list: disposedCases, Icon: CheckCircle2, iconBg: "#FDECEC", iconColor: "#DC2626", route: "/portal/disposedcases/disposed", action: "View all" },
                { key: "todayCases", title: "Today's Cases", list: todayCasesList, Icon: CalendarDays, iconBg: "#EFF6FF", iconColor: "#2563EB", route: "/portal/calendar", action: "View calendar" },
              ].map((b) => {
                const locked = !isEnabled(b.key);
                const prefs = builtinPrefs[b.key] || {};
                const accent = prefs.accentColor || b.iconColor;
                const basis = prefs.subcardField || "courtName";
                const displayTitle = prefs.title || b.title;
                const cardBg = /^#[0-9a-fA-F]{6}$/.test(prefs.bgColor || "") ? prefs.bgColor : "#FFFFFF";
                // Plain count by default; when the user enables the breakdown
                // in the edit dialog (like a custom multilevel metric), the
                // first row of the chosen basis renders as inline chips too.
                // Full stats always open in the click modal.
                const chips = prefs.multilevel
                  ? groupField(b.list, basis)
                      .filter((g) => g.label !== "—")
                      .slice(0, 2)
                      .map((g) => ({ ...g, display: basis === "courtName" ? shortCourt(g.label) : g.label }))
                  : [];
                return (
                  <div
                    key={b.key}
                    role="button"
                    tabIndex={0}
                    title={prefs.description || undefined}
                    onClick={(e) => {
                      if (locked) { showLocked(b.title)(e); return; }
                      setStatsCard({ title: displayTitle, cases: b.list, route: b.route, basis });
                    }}
                    className="metric-tile group relative flex w-[240px] shrink-0 cursor-pointer flex-col rounded-[14px] border border-[#ECEEF5] p-3 transition hover:shadow-[0_6px_18px_rgba(15,23,42,.08)]"
                    style={{ borderTop: `3px solid ${accent}`, background: cardBg }}
                  >
                    {/* Edit / reset controls reveal on hover, like the custom cards. */}
                    <div className="metric-tile-controls absolute right-1 top-[-10px] z-30 flex gap-1 text-xs opacity-0 transition-opacity group-hover:opacity-100">
                      <i
                        className="fa-solid fa-pencil cursor-pointer rounded-full bg-white p-2 text-black shadow hover:bg-gray-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditBuiltin({ key: b.key, baseTitle: b.title, defaultColor: b.iconColor, list: b.list });
                        }}
                      ></i>
                      {builtinPrefs[b.key] && (
                        <button
                          type="button"
                          title="Reset card to default"
                          className="cursor-pointer rounded-full bg-white p-1.5 text-black shadow hover:bg-gray-100"
                          onClick={(e) => { e.stopPropagation(); resetBuiltinCard(b.key); }}
                        >
                          <RotateCcw size={13} />
                        </button>
                      )}
                    </div>
                    <div className="flex items-start justify-between">
                      <div className="min-w-0">
                        <p className="truncate text-[11px] font-medium text-[#737A8C]" title={displayTitle}>{displayTitle}</p>
                        <p className="metric-card-count mt-1 text-[19px] font-bold leading-none" style={{ color: prefs.accentColor || "#060125" }}>{b.key === "todayCases" ? todayCasesCount : b.list.length}</p>
                      </div>
                      <span
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
                        style={prefs.accentColor ? { background: `${accent}1f`, color: accent } : { background: b.iconBg, color: b.iconColor }}
                      >
                        <b.Icon size={19} />
                      </span>
                    </div>
                    {chips.length > 0 && (
                      <div className="mt-1.5 grid grid-cols-2 gap-1">
                        {chips.map((s) => (
                          <div key={s.label} className="rounded bg-black/5 px-1.5 py-0.5 text-left">
                            <p className="truncate text-[10px] text-gray-600" title={s.label}>{s.display}</p>
                            <p className="text-xs font-bold text-gray-800">{s.value}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Built-in cards carry no chip grid, so the link is pushed
                        to the bottom (mt-auto) — otherwise it floats mid-card
                        while the custom cards' links sit on the base line. */}
                    <button
                      type="button"
                      className="mt-auto self-start pt-2 text-[11px] font-medium text-indigo-600 hover:underline"
                      onClick={(e) => { e.stopPropagation(); if (locked) { showLocked(b.title)(e); return; } navigate(b.route); }}
                    >
                      {b.action} &gt;
                    </button>
                    {locked && <LockBadge />}
                  </div>
                );
              })}

              {metricCardList.map((card, index) => {
                const ctx = { uid: globalUser?.uid };
                const matched = filterCases(totalCase, card, ctx);
                const accent = card.accentColor || "#eab308";
                const { groups: subs } = card.multilevel && card.subcardField
                  ? groupForSubcard(totalCase, card, ctx, labelsMap)
                  : { groups: [] };
                // Inline preview: first row of the card's own grouping, falling
                // back to a court-wise split. Full stats open in the modal.
                const chips = (subs.length > 0 ? subs : groupCounts(matched, "courtName"))
                  .filter((g) => g.label !== "—")
                  .slice(0, 2);
                return (
                  <div
                    key={card.id}
                    role="button"
                    tabIndex={0}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={handleDragOver}
                    onDrop={() => handleDrop(index)}
                    title={card.description || card.title}
                    onClick={() => setStatsCard({ title: card.title, cases: matched, route: `/portal/metric/${card.id}`, card })}
                    className="metric-tile group relative flex w-[240px] shrink-0 cursor-pointer flex-col rounded-[14px] border border-[#ECEEF5] bg-white p-3 transition hover:shadow-[0_6px_18px_rgba(15,23,42,.08)]"
                    style={{ borderTop: `3px solid ${accent}` }}
                  >
                    {/* Edit / delete controls reveal on hover of the card. */}
                    <div className="metric-tile-controls absolute right-1 top-[-10px] z-30 flex gap-1 text-xs opacity-0 transition-opacity group-hover:opacity-100">
                      <i
                        className="fa-solid fa-pencil cursor-pointer rounded-full bg-white p-2 text-black shadow hover:bg-gray-100"
                        onClick={(e) => { e.stopPropagation(); setEditMetricId(card.id); }}
                      ></i>
                      <i
                        className="fa-solid fa-trash cursor-pointer rounded-full bg-white p-2 text-black shadow hover:bg-gray-100"
                        onClick={(e) => { e.stopPropagation(); handleDelete(card.id); }}
                      ></i>
                    </div>
                    <div className="flex items-start justify-between">
                      <div className="min-w-0">
                        <p className="truncate text-[11px] font-medium text-[#737A8C]" title={card.title}>{card.title}</p>
                        <p className="metric-card-count mt-1 text-[19px] font-bold leading-none" style={{ color: accent }}>{matched.length}</p>
                      </div>
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full" style={{ background: `${accent}1f`, color: accent }}>
                        <BarChart3 size={19} />
                      </span>
                    </div>
                    {chips.length > 0 && (
                      <div className="mt-1.5 grid grid-cols-2 gap-1">
                        {chips.map((s) => (
                          <div key={s.key || s.label} className="rounded bg-black/5 px-1.5 py-0.5 text-left">
                            <p className="truncate text-[10px] text-gray-600" title={s.label}>{shortCourt(s.label)}</p>
                            <p className="text-xs font-bold text-gray-800">{s.value}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    <button
                      type="button"
                      className="mt-2 self-start text-[11px] font-medium text-indigo-600 hover:underline"
                      onClick={(e) => { e.stopPropagation(); navigate(`/portal/metric/${card.id}`); }}
                    >
                      View all &gt;
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Scroll affordance for the metric strip: a soft fade on whichever
                side still has hidden cards, with a small nudge arrow inside it.
                pointer-events-none on the fade keeps the cards' hover controls
                clickable underneath. */}
            {scrollCards && (
              <>
                <div
                  className={`pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-[#f5f7fa] via-[#f5f7fa]/70 to-transparent transition-opacity duration-200 ${
                    metricsOverflow.left ? "opacity-100" : "opacity-0"
                  }`}
                />
                <div
                  className={`pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-[#f5f7fa] via-[#f5f7fa]/70 to-transparent transition-opacity duration-200 ${
                    metricsOverflow.right ? "opacity-100" : "opacity-0"
                  }`}
                />
                <button
                  type="button"
                  aria-label="Scroll metrics left"
                  onClick={() => scrollMetrics(-1)}
                  className={`absolute left-0 top-1/2 z-20 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full border border-[#E4E7F0] bg-white/90 text-slate-500 shadow-sm transition-opacity duration-200 hover:bg-white hover:text-slate-700 ${
                    metricsOverflow.left ? "opacity-80" : "pointer-events-none opacity-0"
                  }`}
                >
                  <ChevronLeft size={15} />
                </button>
                <button
                  type="button"
                  aria-label="Scroll metrics right"
                  onClick={() => scrollMetrics(1)}
                  className={`absolute right-0 top-1/2 z-20 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full border border-[#E4E7F0] bg-white/90 text-slate-500 shadow-sm transition-opacity duration-200 hover:bg-white hover:text-slate-700 ${
                    metricsOverflow.right ? "opacity-80" : "pointer-events-none opacity-0"
                  }`}
                >
                  <ChevronRight size={15} />
                </button>
              </>
            )}
            </div>
          </div>

          <div className="dashboard-main">

            <div className="dashboard-left">

              <div className="dashboard-row">

                <UpcomingHearings />

                <TasksDueToday />

              </div>
              <div className="dashboard-bottom-row">
                <CaseOverview />

                <RecentCases />

                <MyActivity />
              </div>

             </div>
             <div className="dashboard-right">

              <QuickActions />

              <RecentUpdates />

             </div>

          </div>

        </CRMLayout>

        {/* AI help assistant — floats on the Dashboard only (not other portal
            pages). Rendered outside CRMLayout so its fixed positioning isn't
            offset by that wrapper's `zoom`. */}
        <HelpChatbot />

        {/* Welcome Popup Modal */}
        {/* Free-plan celebration popup — first-time welcome (covers Google signup)
            or an admin-assigned free plan. Duration reflects the actual grant. */}
        <CelebrationModal
          isOpen={showWelcomePopup}
          durationDays={welcomeDays}
          onClose={handleCloseWelcomePopup}
        />

        {/* Case Limit Dialog */}
        <CaseLimitDialog
          isOpen={showCaseLimitDialog}
          onClose={() => setShowCaseLimitDialog(false)}
          currentCount={caseLimitData.currentCount}
          maxLimit={caseLimitData.maxLimit}
          planName={caseLimitData.planName}
        />

        {/* AI Chatbot Upgrade Dialog */}
        <AIChatbotUpgradeDialog
          isOpen={showAIChatbotDialog}
          onClose={() => setShowAIChatbotDialog(false)}
        />

        {/* Mandatory Profile Completion Popup */}
        <ProfileCompletionPopup
          isOpen={showProfilePopup}
          onComplete={() => setShowProfilePopup(false)}
        />

        {/* Generic feature-locked upgrade dialog */}
        <FeatureLockedDialog
          isOpen={!!lockedFeatureName}
          featureName={lockedFeatureName}
          onClose={() => setLockedFeatureName(null)}
        />

        {/* Admin-authored in-app announcement popup */}
        {announcement && (
          <AnnouncementPopup
            announcement={announcement}
            uid={globalUser?.uid}
            vars={{
              name: globalData?.name || globalData?.displayName || "",
              email: globalData?.email || globalUser?.email || "",
              firmName: globalData?.firmName || "",
            }}
            onClose={() => setAnnouncement(null)}
          />
        )}

        {showAddMetric && (
          <CaseModal
            cases={totalCase}
            nextOrder={metricCardList.length}
            onClose={() => setShowAddMetric(false)}
            onSaved={() => { setShowAddMetric(false); refreshMetricCards(); }}
          />
        )}

        {editMetricId && (
          <EditCaseModal
            metricId={editMetricId}
            cases={totalCase}
            onClose={() => setEditMetricId(null)}
            onSaved={() => { setEditMetricId(null); refreshMetricCards(); }}
          />
        )}

        {/* Edit dialog for the built-in cards — the SAME MetricCardForm the
            custom metrics use, with Filters hidden (their case list is fixed).
            Per-card reset lives on the card's hover RotateCcw control. */}
        {editBuiltin && (
          <MetricCardForm
            mode="edit"
            hideFilters
            labels={Object.entries(labelsMap).map(([id, name]) => ({ id, name }))}
            cases={editBuiltin.list}
            uid={globalUser?.uid}
            initial={{
              title: builtinPrefs[editBuiltin.key]?.title || editBuiltin.baseTitle,
              description: builtinPrefs[editBuiltin.key]?.description || "",
              accentColor: builtinPrefs[editBuiltin.key]?.accentColor || editBuiltin.defaultColor,
              bgColor: builtinPrefs[editBuiltin.key]?.bgColor || "#FFFFFF",
              multilevel: !!builtinPrefs[editBuiltin.key]?.multilevel,
              subcardField: builtinPrefs[editBuiltin.key]?.subcardField || "",
              filters: [],
            }}
            onClose={() => setEditBuiltin(null)}
            onSubmit={saveBuiltinCard}
          />
        )}

        {/* Stats modal — opened by clicking a stat card body. Shows the card's
            full breakdown (its own grouping for custom multilevel cards, plus
            court / case-type / status splits) with a link to the full list. */}
        {statsCard && (() => {
          const subKey = statsCard.card?.subcardField || statsCard.basis || null;
          const subDef = subKey ? SUBCARD_FIELDS.find((f) => f.key === subKey) : null;
          const sections = [];
          if (subDef && !["courtName", "caseType", "status"].includes(subKey)) {
            sections.push({ title: `By ${subDef.label}`, rows: groupField(statsCard.cases, subKey) });
          }
          sections.push(
            { title: "By Court", rows: groupField(statsCard.cases, "courtName") },
            { title: "By Case Type", rows: groupField(statsCard.cases, "caseType") },
            { title: "By Status", rows: groupField(statsCard.cases, "status") },
          );
          return (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4"
              onClick={() => setStatsCard(null)}
            >
              <div
                className="flex max-h-[80vh] w-full max-w-lg flex-col rounded-xl bg-white shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start justify-between border-b border-slate-100 p-4">
                  <div>
                    <h3 className="text-base font-bold text-slate-900">{statsCard.title}</h3>
                    <p className="mt-0.5 text-xs text-slate-500">{statsCard.cases.length} case(s)</p>
                  </div>
                  <button
                    onClick={() => setStatsCard(null)}
                    className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                    aria-label="Close stats"
                  >
                    <X size={18} />
                  </button>
                </div>
                <div className="space-y-4 overflow-y-auto p-4">
                  {sections.map((s) => (
                    <div key={s.title}>
                      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{s.title}</p>
                      {s.rows.length === 0 ? (
                        <p className="text-xs text-slate-400">No data</p>
                      ) : (
                        <div className="space-y-1">
                          {s.rows.map((r) => (
                            <div key={r.key || r.label} className="flex items-center justify-between gap-3 rounded-md bg-slate-50 px-2.5 py-1.5">
                              <span className="truncate text-xs text-slate-700" title={r.label}>{r.label}</span>
                              <span className="text-xs font-bold text-slate-900">{r.value}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex justify-end border-t border-slate-100 p-3">
                  <button
                    onClick={() => { const r = statsCard.route; setStatsCard(null); navigate(r); }}
                    className="rounded-lg bg-yellow-500 px-3 py-1.5 text-xs font-semibold text-gray-900 hover:bg-yellow-600"
                  >
                    View all cases
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Reset metric cards confirmation */}
        {showResetConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 px-4">
            <div className="bg-white text-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full">
              <h3 className="text-lg font-bold mb-3">Reset Metric Cards</h3>
              <p className="text-gray-600 mb-6">
                This removes all{" "}
                <span className="font-semibold">{metricCardList.length}</span>{" "}
                custom metric card(s) and resets the built-in cards (Pending, Disposed, Today's) to their default colour and stats. This cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowResetConfirm(false)}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetDefault}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        )}

      </>
    );
  }

  export default Dashboard;
